'use client';

import { useState } from 'react';
import Image from 'next/image';
import { debugError } from '../lib/debug-logger';
import Button from '@/components/ui/Button';

export default function AboutCreator() {
  const [feedback, setFeedback] = useState('');
  const [wantsReply, setWantsReply] = useState(false);
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const response = await fetch('/api/feedback', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          feedback,
          wantsReply,
          email: wantsReply ? email : undefined,
        }),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFeedback('');
        setEmail('');
        setWantsReply(false);
      } else {
        try {
          const text = await response.text();
          debugError('Feedback submission error - Response:', text);
          debugError('Response status:', response.status);
          debugError('Response headers:', response.headers);
          
          // Try to parse as JSON if possible
          try {
            const data = JSON.parse(text);
            debugError('Parsed error:', data);
          } catch {
            // Not JSON, that's ok
          }
        } catch (parseError) {
          debugError('Failed to read error response:', parseError);
        }
        setSubmitStatus('error');
      }
    } catch (error) {
      debugError('Feedback submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white border border-[#16223d]/10 ring-1 ring-[#16223d]/5 rounded-2xl shadow-[0_4px_6px_rgba(28,35,33,0.06),0_24px_48px_rgba(28,35,33,0.08)] p-5 flex flex-col h-full">
      <div className="flex items-center gap-3 mb-3">
        <div className="shrink-0">
          <div className="relative w-16 h-16">
            <Image
              src="/friendly-headshot.jpg"
              alt="Jules Lemée"
              fill
              sizes="4rem"
              className="rounded-full object-cover"
            />
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="text-lg font-bold text-foreground mb-0.5">Hi, I&apos;m Jules</h2>
          <p className="text-sm text-foreground/60 leading-snug">
            I built this quiz to help bridge political divides and cut through apathy, especially among young people.{' '}
            <a
              href="https://juleslemee.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:text-primary-dark font-semibold transition-colors"
            >
              juleslemee.com
            </a>
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="grow flex flex-col gap-2.5">
        <textarea
          value={feedback}
          onChange={(e) => setFeedback(e.target.value)}
          placeholder="Feedback, ideas, or questions..."
          className="w-full min-h-[64px] grow p-3 border border-gray-300 rounded-lg resize-none bg-white text-foreground placeholder-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
          required
        />
        {wantsReply && (
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Your email address"
            className="w-full p-2.5 border border-gray-300 rounded-lg bg-white text-foreground placeholder-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
            required={wantsReply}
          />
        )}
        <div className="flex items-center justify-between gap-3">
          <label htmlFor="wantsReply" className="flex items-center gap-2 text-sm text-foreground/60 cursor-pointer select-none">
            <input
              type="checkbox"
              id="wantsReply"
              checked={wantsReply}
              onChange={(e) => setWantsReply(e.target.checked)}
              className="w-4 h-4 text-primary rounded border-gray-300 focus:ring-primary focus:ring-2"
            />
            Email me back
          </label>
          <Button
            type="submit"
            disabled={isSubmitting}
            className="shrink-0 py-2.5"
          >
            {isSubmitting ? 'Sending...' : 'Send Feedback'}
          </Button>
        </div>
        {submitStatus === 'success' && (
          <p className="text-green-600 text-sm">Thanks for the feedback.</p>
        )}
        {submitStatus === 'error' && (
          <p className="text-red-600 text-sm">Something went wrong. Please try again.</p>
        )}
      </form>
    </div>
  );
}